// oxlint-disable complexity
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, REQUEST, RESPONSE_INIT } from '@angular/core';
import { parseCookiesFromString } from '@hyundai/shared-lib';
import { setHeader } from '../utilities/redirect';

const SECONDS_PER_DAY = 24 * 60 * 60;

@Injectable({
    providedIn: 'root',
})
export class CookieService {
    protected readonly document = inject(DOCUMENT);
    protected readonly platformId = inject(PLATFORM_ID);
    protected readonly request = inject(REQUEST, { optional: true });
    protected readonly responseInit = inject(RESPONSE_INIT, { optional: true });

    get(name: string): string | undefined {
        return this.parseCookies()[name];
    }

    has(name: string): boolean {
        return Boolean(this.get(name));
    }

    getAll(): Record<string, string> {
        return this.parseCookies();
    }

    set(name: string, value: string, expireDays: number, path = '/', crossSite = false): void {
        const maxAgeSeconds = expireDays * SECONDS_PER_DAY;
        const sameSiteAttr = crossSite ? '; SameSite=None; Secure' : '';
        const cookieValue = `${name}=${value}; Max-Age=${maxAgeSeconds}; Path=${path}${sameSiteAttr}`;

        if (isPlatformBrowser(this.platformId)) {
            this.document.cookie = cookieValue;
            return;
        }

        if (this.responseInit) {
            setHeader(this.responseInit, 'Set-Cookie', cookieValue);
        }
    }

    delete(name: string): void {
        this.set(name, '', 0);
    }

    protected parseCookies(): Record<string, string> {
        const cookies: Record<string, string> = {};
        const cookieString = isPlatformBrowser(this.platformId)
            ? this.document.cookie
            : (this.request?.headers.get('cookie') ?? '');

        if (!cookieString) {
            return cookies;
        }

        return parseCookiesFromString(cookieString);
    }
}
